import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useAccount } from '@/contexts/AccountContext';
import { Loader2 } from "lucide-react";
import { AccountType } from '@/types/account';

const AccountTypePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { setAccountType } = useAccount();
  const [loading, setLoading] = useState(false);

  const handleAccountTypeSelect = async (type: AccountType) => {
    if (!user) {
      toast({
        title: "Error",
        description: "Please sign in to continue",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // First, check if profile exists
      const { data: existingProfile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Profile check error:', profileError);
        throw profileError;
      }

      if (existingProfile) {
        // Update existing profile
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ 
            account_type: type,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);

        if (updateError) {
          console.error('Profile update error:', updateError);
          throw updateError;
        }
      } else {
        // Create new profile
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            account_type: type,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          console.error('Profile creation error:', insertError);
          throw insertError;
        }
      }

      // Set account type in context
      setAccountType(type);

      toast({
        title: "Success",
        description: "Account type set successfully",
      });

      navigate('/dashboard');
    } catch (error: any) {
      console.error('Account setup error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to set up account",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-2xl p-4 sm:p-8">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Choose Your Account Type</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Select the type of account that best suits your needs</p>
        </div>

        <div className="grid gap-4 sm:gap-6">
          <Button
            variant="outline"
            className="h-auto py-4 sm:py-6 px-4 sm:px-8 flex flex-col items-center gap-2 sm:gap-4"
            onClick={() => handleAccountTypeSelect('personal')}
            disabled={loading}
          >
            <div className="text-xl sm:text-2xl">👤</div>
            <div className="text-base sm:text-lg font-semibold">Personal</div>
            <div className="text-xs sm:text-sm text-muted-foreground text-center">
              Perfect for individual users managing their personal finances
            </div>
          </Button>

          <Button
            variant="outline"
            className="h-auto py-4 sm:py-6 px-4 sm:px-8 flex flex-col items-center gap-2 sm:gap-4 opacity-50 cursor-not-allowed"
            disabled
          >
            <div className="text-xl sm:text-2xl">👨‍👩‍👧‍👦</div>
            <div className="text-base sm:text-lg font-semibold">Family</div>
            <div className="text-xs sm:text-sm text-muted-foreground text-center">
              Coming soon! Manage shared expenses with your family
            </div>
          </Button>

          <Button
            variant="outline"
            className="h-auto py-4 sm:py-6 px-4 sm:px-8 flex flex-col items-center gap-2 sm:gap-4 opacity-50 cursor-not-allowed"
            disabled
          >
            <div className="text-xl sm:text-2xl">💼</div>
            <div className="text-base sm:text-lg font-semibold">Business</div>
            <div className="text-xs sm:text-sm text-muted-foreground text-center">
              Coming soon! Track business expenses and income
            </div>
          </Button>
        </div>

        {loading && (
          <div className="mt-6 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
      </Card>
    </div>
  );
};

export default AccountTypePage; 